import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "@/features/auth/stores/authStore";

/**
 * Cliente HTTP central do portal das imobiliárias (imobgo-agency-web).
 *
 * Base = `VITE_API_URL` (ex.: `http://localhost:5002/v1`), já com o prefixo de
 * versão. Os módulos `features` chamam caminhos relativos (`/dashboard/summary`).
 *
 * - Request: injeta `Authorization: Bearer <accessToken>` do authStore.
 * - Response: em 401, tenta UMA renovação de token (refresh) e refaz a chamada;
 *   se a renovação falhar, faz logout. A renovação é compartilhada entre chamadas
 *   concorrentes (uma única promise por vez).
 */
const baseURL = import.meta.env.VITE_API_URL ?? "http://localhost:5002/v1";

export const http: AxiosInstance = axios.create({
  baseURL,
  headers: { "Content-Type": "application/json" },
  // Necessário para enviar/receber o cookie HttpOnly `rt` (refresh token) — ADR-0017.
  withCredentials: true,
});

http.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/** Renovação em andamento — evita disparar vários /auth/refresh em paralelo. */
let refreshing: Promise<string | null> | null = null;

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    if (status === 401 && original && !original._retried) {
      original._retried = true;
      refreshing ??= useAuthStore
        .getState()
        .refresh()
        .finally(() => {
          refreshing = null;
        });

      const newToken = await refreshing;
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return http(original);
      }
      useAuthStore.getState().logout();
    }

    return Promise.reject(error);
  },
);

/** Extrai uma mensagem amigável de um erro de API (ProblemDetails / `{ message }`). */
export function apiErrorMessage(error: unknown, fallback = "Algo deu errado. Tente novamente."): string {
  if (error instanceof AxiosError) {
    const data = error.response?.data as { message?: string; detail?: string; title?: string } | undefined;
    return data?.message ?? data?.detail ?? data?.title ?? error.message ?? fallback;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}
